/**
 * SmokeTestCard
 *
 * Admin QA card that runs every download smoke test against the currently
 * selected client and shows a live results table.
 *
 * Context resolution (in order):
 *   clientId       - app.selectedClient
 *   providerId     - first affiliated provider for the client
 *   organizationId - first org associated with the client
 *   servicelineId  - service line named "General Medicine Dx", else first available
 *   dateFrom/To    - app.dateRange
 */

import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withStyles } from '@mui/styles';
import moment from 'moment';
import {
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Icon,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import { Link } from 'react-router-dom';
import fileDownload from 'js-file-download';
import axiosInstance from 'utils/axiosUtil';
import { StringUtil } from 'utils';
import { StoreUtil } from 'doctivity-shared/utils';
import { FindProviderDialog } from 'components';
import { FindOrganizationDialog } from 'organizations';
import { FindLocationDialog } from 'locations';
import { smokeTests } from './smokeTests';

const ENTITIES = [
  { key: 'provider',  label: 'Provider' },
  { key: 'org',       label: 'Organization' },
  { key: 'location',  label: 'Location' },
];

// ── Status constants ──────────────────────────────────────────────────────────

const STATUS = {
  PENDING: 'pending',
  RUNNING: 'running',
  PASS: 'pass',
  FAIL: 'fail',
  SKIPPED: 'skipped',
};

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = (theme) => ({
  card: {
    marginBottom: theme.spacing(3),
  },
  cardInnerHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  cardTitle: {
    flex: 1,
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  entityToggle: {
    marginBottom: theme.spacing(1),
    '& .MuiToggleButton-root': {
      textTransform: 'none',
      padding: '4px 14px',
      fontSize: 13,
    },
  },
  summary: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
  progress: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  table: {
    marginTop: theme.spacing(2),
  },
  tableHead: {
    backgroundColor: theme.palette.grey[100],
  },
  tableHeadCell: {
    fontWeight: 700,
    fontSize: 13,
  },
  tableCell: {
    fontSize: 13,
    verticalAlign: 'middle',
  },
  chipPass: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    fontWeight: 600,
    fontSize: 12,
  },
  chipFail: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    fontWeight: 600,
    fontSize: 12,
  },
  chipSkipped: {
    backgroundColor: '#f5f5f5',
    color: '#757575',
    fontSize: 12,
  },
  chipRunning: {
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
    fontSize: 12,
  },
  chipPending: {
    backgroundColor: '#fafafa',
    color: '#9e9e9e',
    fontSize: 12,
  },
  errorText: {
    fontSize: 11,
    color: '#c62828',
    maxWidth: 300,
    wordBreak: 'break-word',
  },
  contextPanel: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    alignItems: 'center',
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: 4,
    marginBottom: theme.spacing(2),
  },
  contextItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  contextLabel: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    fontWeight: 600,
    marginRight: theme.spacing(0.5),
  },
  contextValue: {
    fontSize: 12,
    color: theme.palette.text.primary,
  },
  contextResolving: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
  contextInfo: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
    fontStyle: 'italic',
  },
  noResults: {
    padding: theme.spacing(3),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
});

// ── Helpers ───────────────────────────────────────────────────────────────────

function responseSize(response) {
  try {
    const data = response.data;
    if (!data) return 0;
    if (typeof data === 'string') return data.length;
    if (data instanceof Blob) return data.size;
    if (data instanceof ArrayBuffer) return data.byteLength;
    return JSON.stringify(data).length;
  } catch {
    return 0;
  }
}

function formatBytes(n) {
  if (n === 0) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Component ─────────────────────────────────────────────────────────────────

class SmokeTestCard extends Component {
  constructor(props) {
    super(props);

    this.state = {
      // 'idle' | 'resolving' | 'running' | 'done'
      runState: 'idle',
      selectedEntity: 'provider',
      results: [],
      context: null,
      contextError: null,
      completedAt: null,
      responseData: {},
      // Serviceline resolved on mount (for claims tests)
      defaultServicelineId: null,
      // Explicit entity picks (null = not yet selected)
      overrideProvider: null,
      overrideOrg: null,
      overrideLocation: null,
      // Dialog open state
      providerDialogOpen: false,
      orgDialogOpen: false,
      locationDialogOpen: false,
    };
  }

  componentDidMount() {
    this.resolveDefaults();
  }

  // ── Resolve defaults on mount ─────────────────────────────────────────────

  resolveDefaults = async () => {
    const { client } = this.props;

    // Only pre-resolve the service line (needed for claims tests).
    // Provider, org, and location must be selected explicitly by the user.
    let defaultServicelineId = null;
    if (client && client.has_claims_access) {
      try {
        const res = await axiosInstance.get('/ClaimsServicelines', { params: { opts: { order: [['name', 'ASC']] } } });
        const rows = res.data?.rows || res.data || [];
        const genMed = rows.find((r) => /general medicine/i.test(r.name || ''));
        defaultServicelineId = genMed ? genMed.id : (rows[0]?.id ?? null);
      } catch (err) {
        console.warn('QA: could not resolve serviceline', err);
      }
    }

    this.setState({ defaultServicelineId });
  };

  // ── Dialog handlers ───────────────────────────────────────────────────────

  openProviderDialog = () => this.setState({ providerDialogOpen: true });
  closeProviderDialog = () => this.setState({ providerDialogOpen: false });
  onProviderSelected = (provider) => {
    this.setState({ overrideProvider: provider, providerDialogOpen: false });
  };

  openOrgDialog = () => this.setState({ orgDialogOpen: true });
  closeOrgDialog = () => this.setState({ orgDialogOpen: false });
  onOrgSelected = (org) => {
    this.setState({ overrideOrg: org, orgDialogOpen: false });
  };

  openLocationDialog = () => this.setState({ locationDialogOpen: true });
  closeLocationDialog = () => this.setState({ locationDialogOpen: false });
  onLocationSelected = (location) => {
    this.setState({ overrideLocation: location, locationDialogOpen: false });
  };

  // ── Context resolution ─────────────────────────────────────────────────────

  resolveContext = async () => {
    const { app, client } = this.props;
    const { overrideProvider, overrideOrg, overrideLocation, defaultServicelineId } = this.state;
    const clientId = app.selectedClient;

    const ctx = {
      clientId,
      providerId: overrideProvider?.id ?? null,
      organizationId: overrideOrg?.id ?? null,
      locationId: overrideLocation?.id ?? null,
      servicelineId: defaultServicelineId ?? null,
      dateFrom: moment(app.dateRange.start).startOf('day').toISOString(),
      dateTo: moment(app.dateRange.end).endOf('day').toISOString(),
    };

    // Fallback serviceline resolution if not already cached
    if (client && client.has_claims_access && !ctx.servicelineId) {
      try {
        const res = await axiosInstance.get('/ClaimsServicelines', { params: { opts: { order: [['name', 'ASC']] } } });
        const rows = res.data?.rows || res.data || [];
        const genMed = rows.find((r) => /general medicine/i.test(r.name || ''));
        ctx.servicelineId = genMed ? genMed.id : (rows[0]?.id ?? null);
      } catch (err) {
        console.warn('QA: could not resolve serviceline', err);
      }
    }

    return ctx;
  };

  // ── Run all tests ──────────────────────────────────────────────────────────

  runTests = async () => {
    const { client } = this.props;
    const { selectedEntity } = this.state;
    const hasClaimsAccess = !!(client && client.has_claims_access);
    const activeTests = smokeTests[selectedEntity] || [];

    this.setState({
      runState: 'resolving',
      results: activeTests.map((t) => ({
        id: t.id,
        name: t.name,
        path: null, // resolved after context is ready
        status: STATUS.PENDING,
        httpStatus: null,
        size: null,
        duration: null,
        error: null,
      })),
      context: null,
      contextError: null,
      completedAt: null,
      responseData: {},
    });

    let ctx;
    try {
      ctx = await this.resolveContext();
      // Resolve paths now that we have context
      this.setState((prev) => ({
        context: ctx,
        runState: 'running',
        results: prev.results.map((r) => {
          const test = activeTests.find((t) => t.id === r.id);
          return { ...r, path: test && test.path ? test.path(ctx) : null };
        }),
      }));
    } catch (err) {
      this.setState({
        runState: 'done',
        contextError: `Could not resolve test context: ${err.message}`,
      });
      return;
    }

    const responseData = {};

    for (const test of activeTests) {
      // Mark as running
      this.setState((prev) => ({
        results: prev.results.map((r) =>
          r.id === test.id ? { ...r, status: STATUS.RUNNING } : r,
        ),
      }));

      // Skip claims tests if no access
      if (test.requiresClaims && !hasClaimsAccess) {
        this.setState((prev) => ({
          results: prev.results.map((r) =>
            r.id === test.id
              ? { ...r, status: STATUS.SKIPPED, error: 'No claims access' }
              : r,
          ),
        }));
        continue;
      }

      const start = Date.now();
      try {
        const response = await test.run(axiosInstance, ctx);
        const duration = Date.now() - start;
        const size = responseSize(response);
        const isEmpty = size === 0;

        if (isEmpty) {
          this.setState((prev) => ({
            results: prev.results.map((r) =>
              r.id === test.id
                ? {
                    ...r,
                    status: STATUS.FAIL,
                    httpStatus: response.status,
                    size,
                    duration,
                    error: 'No data returned',
                  }
                : r,
            ),
          }));
        } else {
          responseData[test.id] = response.data;
          this.setState((prev) => ({
            results: prev.results.map((r) =>
              r.id === test.id
                ? {
                    ...r,
                    status: STATUS.PASS,
                    httpStatus: response.status,
                    size,
                    duration,
                  }
                : r,
            ),
          }));
        }
      } catch (err) {
        const duration = Date.now() - start;
        const httpStatus = err.response?.status ?? null;
        this.setState((prev) => ({
          results: prev.results.map((r) =>
            r.id === test.id
              ? {
                  ...r,
                  status: STATUS.FAIL,
                  httpStatus,
                  duration,
                  error: err.response?.data?.message || err.message || 'Unknown error',
                }
              : r,
          ),
        }));
      }
    }

    this.setState({
      runState: 'done',
      completedAt: new Date(),
      responseData,
    });
  };


  // ── Download single test output ────────────────────────────────────────────

  downloadSingle = (testId) => {
    const { responseData } = this.state;
    const data = responseData[testId];
    if (data == null) return;

    const isPdf = testId === 'provider_pdf';
    const ext = isPdf ? 'pdf' : 'csv';
    let fileData = data;
    if (isPdf) {
      try { fileData = StringUtil.b64toBlob(data, 'application/pdf'); } catch { /* already a blob */ }
    }
    fileDownload(fileData, `${testId}.${ext}`);
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  renderStatusChip(status) {
    const { classes } = this.props;
    switch (status) {
      case STATUS.PASS:
        return <Chip label='✓ Pass' size='small' className={classes.chipPass} />;
      case STATUS.FAIL:
        return <Chip label='✗ Fail' size='small' className={classes.chipFail} />;
      case STATUS.SKIPPED:
        return <Chip label='⏭ Skipped' size='small' className={classes.chipSkipped} />;
      case STATUS.RUNNING:
        return (
          <Chip
            label='Running…'
            size='small'
            className={classes.chipRunning}
            icon={<CircularProgress size={10} style={{ color: '#1565c0', marginLeft: 6 }} />}
          />
        );
      default:
        return <Chip label='Pending' size='small' className={classes.chipPending} />;
    }
  }

  render() {
    const { classes, client, app, user } = this.props;
    const {
      runState,
      results,
      context,
      contextError,
      completedAt,
      selectedEntity,
      overrideProvider,
      overrideOrg,
      overrideLocation,
      providerDialogOpen,
      orgDialogOpen,
      locationDialogOpen,
    } = this.state;

    const hasClaimsAccess = !!(client && client.has_claims_access);
    const isRunning = runState === 'running' || runState === 'resolving';

    const passed = results.filter((r) => r.status === STATUS.PASS).length;
    const failed = results.filter((r) => r.status === STATUS.FAIL).length;
    const skipped = results.filter((r) => r.status === STATUS.SKIPPED).length;
    const total = results.length;
    const completed = results.filter(
      (r) => r.status !== STATUS.PENDING && r.status !== STATUS.RUNNING,
    ).length;

    return (
      <Card className={classes.card} elevation={2}>
        <CardContent>
          <div className={classes.cardInnerHeader}>
            <Typography
              component='h2'
              variant='h6'
              color='primary'
              gutterBottom
              className={classes.cardTitle}
            >
              Smoke Test - Downloads
            </Typography>
            <Button
              color='primary'
              size='small'
              onClick={this.runTests}
              disabled={isRunning || (
                selectedEntity === 'provider' ? !overrideProvider :
                selectedEntity === 'org'      ? !overrideOrg :
                selectedEntity === 'location' ? !overrideLocation :
                true
              )}
              startIcon={
                isRunning ? (
                  <CircularProgress size={16} color='inherit' />
                ) : (
                  <Icon fontSize='small'>check_circle</Icon>
                )
              }
            >
              {isRunning ? 'Checking…' : 'Run Check'}
            </Button>
          </div>

          {/* Entity type toggle */}
          <ToggleButtonGroup
            value={selectedEntity}
            exclusive
            size='small'
            className={classes.entityToggle}
            onChange={(_, val) => {
              if (!isRunning && val) this.setState({ selectedEntity: val, results: [], runState: 'idle' });
            }}
          >
            {ENTITIES.map(({ key, label }) => (
              <ToggleButton key={key} value={key} disabled={isRunning}>
                {label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {/* Context panel */}
          <div className={classes.contextPanel}>
            {/* Client — always shown */}
            <div className={classes.contextItem}>
              <Typography className={classes.contextLabel}>Client:</Typography>
              <Typography className={classes.contextValue}>{client?.name || app.selectedClient}</Typography>
            </div>

            {/* Provider — shown only in provider mode */}
            {selectedEntity === 'provider' && (
              <div className={classes.contextItem}>
                <Typography className={classes.contextLabel}>Provider:</Typography>
                {overrideProvider ? (
                  <Typography className={classes.contextValue}>
                    {overrideProvider.first_name} {overrideProvider.last_name}
                  </Typography>
                ) : (
                  <Typography className={classes.contextValue} style={{ color: '#999' }}>None selected</Typography>
                )}
                <Button
                  size='small'
                  color='primary'
                  onClick={this.openProviderDialog}
                  disabled={isRunning}
                  style={{ marginLeft: 4, minWidth: 0, padding: '0 6px', fontSize: 11 }}
                >
                  {overrideProvider ? 'Change' : 'Select'}
                </Button>
              </div>
            )}

            {/* Organization — shown only in org mode */}
            {selectedEntity === 'org' && (
              <div className={classes.contextItem}>
                <Typography className={classes.contextLabel}>Organization:</Typography>
                {overrideOrg ? (
                  <Typography className={classes.contextValue}>{overrideOrg.name}</Typography>
                ) : (
                  <Typography className={classes.contextValue} style={{ color: '#999' }}>None selected</Typography>
                )}
                <Button
                  size='small'
                  color='primary'
                  onClick={this.openOrgDialog}
                  disabled={isRunning}
                  style={{ marginLeft: 4, minWidth: 0, padding: '0 6px', fontSize: 11 }}
                >
                  {overrideOrg ? 'Change' : 'Select'}
                </Button>
              </div>
            )}

            {/* Location — shown only in location mode */}
            {selectedEntity === 'location' && (
              <div className={classes.contextItem}>
                <Typography className={classes.contextLabel}>Location:</Typography>
                {overrideLocation ? (
                  <Typography className={classes.contextValue}>
                    {overrideLocation.name || overrideLocation.address1 || `#${overrideLocation.id}`}
                  </Typography>
                ) : (
                  <Typography className={classes.contextValue} style={{ color: '#999' }}>None selected</Typography>
                )}
                <Button
                  size='small'
                  color='primary'
                  onClick={this.openLocationDialog}
                  disabled={isRunning}
                  style={{ marginLeft: 4, minWidth: 0, padding: '0 6px', fontSize: 11 }}
                >
                  {overrideLocation ? 'Change' : 'Select'}
                </Button>
              </div>
            )}

            {!hasClaimsAccess && (
              <Typography className={classes.contextResolving}>
                · Claims tests will be skipped (client has no claims access)
              </Typography>
            )}
          </div>

          {contextError && (
            <Typography color='error' style={{ marginTop: 8, fontSize: 13 }}>
              {contextError}
            </Typography>
          )}

          {/* Dialogs */}
          <FindProviderDialog
            open={providerDialogOpen}
            onSelect={this.onProviderSelected}
            clientId={app.selectedClient}
            userId={user?.id}
            onClose={this.closeProviderDialog}
          />
          <FindOrganizationDialog
            open={orgDialogOpen}
            onSelect={this.onOrgSelected}
            onClose={this.closeOrgDialog}
          />
          <FindLocationDialog
            open={locationDialogOpen}
            onSelect={this.onLocationSelected}
            onClose={this.closeLocationDialog}
          />

          {/* Progress bar while running */}
          {isRunning && total > 0 && (
            <LinearProgress
              className={classes.progress}
              variant='determinate'
              value={(completed / total) * 100}
            />
          )}

          {/* Summary line */}
          {runState === 'done' && (
            <Typography className={classes.summary}>
              {passed} passed · {failed} failed · {skipped} skipped
              {completedAt && ` · completed ${moment(completedAt).format('h:mm:ss a')}`}
            </Typography>
          )}

          {/* Results table */}
          {results.length > 0 ? (
            <Table size='small' className={classes.table}>
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell className={classes.tableHeadCell}>Test</TableCell>
                  <TableCell className={classes.tableHeadCell}>Status</TableCell>
                  <TableCell className={classes.tableHeadCell}>HTTP</TableCell>
                  <TableCell className={classes.tableHeadCell}>Size</TableCell>
                  <TableCell className={classes.tableHeadCell}>Duration</TableCell>
                  <TableCell className={classes.tableHeadCell}>Error</TableCell>
                  <TableCell className={classes.tableHeadCell} />
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell className={classes.tableCell}>
                      {r.name}
                      {r.path && (
                        <Link
                          to={r.path}
                          target='_blank'
                          rel='noopener noreferrer'
                          style={{ marginLeft: 6, fontSize: 11, color: '#888', whiteSpace: 'nowrap' }}
                        >
                          ({r.path})
                        </Link>
                      )}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {this.renderStatusChip(r.status)}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {r.httpStatus ?? '—'}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {r.size != null ? formatBytes(r.size) : '—'}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {r.duration != null ? `${r.duration} ms` : '—'}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {r.error ? (
                        <Tooltip title={r.error}>
                          <Typography className={classes.errorText} noWrap>
                            {r.error}
                          </Typography>
                        </Tooltip>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell className={classes.tableCell} style={{ width: 40, padding: '0 4px' }}>
                      {r.status === STATUS.PASS && (
                        <Tooltip title='Download'>
                          <IconButton size='small' onClick={() => this.downloadSingle(r.id)} style={{ opacity: 0.5 }}>
                            <Icon fontSize='small'>download</Icon>
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography className={classes.noResults}>
              Select a {ENTITIES.find((e) => e.key === selectedEntity)?.label ?? 'entity'} above, then press &ldquo;Run Tests&rdquo; to begin.
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  }
}

SmokeTestCard.propTypes = {
  classes: PropTypes.object.isRequired,
  app: PropTypes.object.isRequired,
  client: PropTypes.object,
  user: PropTypes.object.isRequired,
};

function mapStateToProps(state) {
  const { app, user, clients } = state;
  const clientList = StoreUtil.get(clients, StoreUtil.COMMON_LIST);
  const client =
    clientList && clientList.data
      ? clientList.data.find((c) => c.id === app.selectedClient)
      : null;

  return { app, user: user || {}, client };
}

const styled = withStyles(styles)(SmokeTestCard);
const connected = connect(mapStateToProps)(styled);
export { connected as SmokeTestCard };
